import React, { Component, ReactNode } from 'react';
import logger from 'app/components/common/ErrorLogger';
import maxHomeLogo from 'assets/logos/maxhome.svg';
import {
  isChunkLoadError,
  requestStaleChunkReload,
} from 'app/utils/staleChunkReload';

interface ErrorBoundaryProps {
  children: ReactNode;
  errorMessage?: string;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logger.captureException(error, {
      component: errorInfo.componentStack ?? undefined,
    });
    // A stale chunk heals with one reload; the guard decides whether to run.
    if (isChunkLoadError(error)) {
      requestStaleChunkReload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      const { errorMessage } = this.props;
      const displayMessage =
        errorMessage ||
        "Oops! something went wrong! We're actively looking into it.";

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
          <img className="h-14 mb-8" src={maxHomeLogo} alt="MaxHome Logo" />
          <h1 className="text-5xl font-bold text-blue-600 mb-4">
            Something went wrong!
          </h1>
          <p className="text-xl text-gray-600 mb-8 text-center max-w-2xl">
            {displayMessage}
          </p>
          <button
            className="px-6 py-3 text-lg font-bold text-white bg-blue-600 hover:bg-blue-700 border-none rounded cursor-pointer transition-colors"
            onClick={() => window.location.reload()}
            style={{ backgroundColor: '#2F78EB' }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
